import {
  Badge,
  Box,
  Button,
  Card,
  Code,
  Flex,
  Heading,
  HStack,
  Input,
  Spinner,
  Table,
  Text,
  VStack,
} from '@chakra-ui/react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  EyeIcon,
  NetworkIcon,
  RefreshCwIcon,
  SearchIcon,
  Trash2Icon,
  XIcon,
} from 'lucide-react'
import { useState } from 'react'
import { k8sApi, type K8sService, type ServicePort } from '~/lib/api'
import { toaster } from '~/toaster'

interface ServicesTabProps {
  namespace: string
  isAdmin: boolean
}

const PAGE_SIZE = 10

// Format ports for display
function formatPorts(ports: ServicePort[]): string {
  if (!ports || ports.length === 0) return '-'
  return ports
    .map((p) => {
      let portStr = `${p.port}`
      if (p.targetPort) portStr += `:${p.targetPort}`
      if (p.nodePort) portStr += `:${p.nodePort}`
      portStr += `/${p.protocol}`
      return portStr
    })
    .join(', ')
}

// View Service Modal
function ViewServiceModal({ service, onClose }: { service: K8sService; onClose: () => void }) {
  return (
    <Box position="fixed" inset={0} bg="blackAlpha.600" zIndex={1000} onClick={onClose}>
      <Box
        position="fixed"
        top="50%"
        left="50%"
        transform="translate(-50%, -50%)"
        bg="bg.panel"
        borderRadius="lg"
        w="90vw"
        maxW="700px"
        maxH="80vh"
        overflow="hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Flex
          justify="space-between"
          align="center"
          p={4}
          borderBottomWidth="1px"
          borderColor="border"
        >
          <VStack align="flex-start" gap={0}>
            <Heading size="md">Service Details</Heading>
            <Text fontSize="sm" color="content.secondary">
              {service.namespace}/{service.name}
            </Text>
          </VStack>
          <Button
            size="sm"
            variant="ghost"
            onClick={onClose}
            aria-label="Close dialog"
            minW="44px"
            minH="44px"
          >
            <XIcon size={16} />
          </Button>
        </Flex>
        <Box overflow="auto" maxH="calc(80vh - 80px)" p={4}>
          <VStack align="stretch" gap={4}>
            <Box>
              <Text fontWeight="semibold" mb={2}>
                Service Info
              </Text>
              <HStack gap={4} flexWrap="wrap">
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Type
                  </Text>
                  <Badge colorPalette={getServiceTypeColor(service.type)}>{service.type}</Badge>
                </Box>
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Cluster IP
                  </Text>
                  <Code fontSize="sm">{service.clusterIP || 'None'}</Code>
                </Box>
                {service.externalIP && (
                  <Box>
                    <Text fontSize="xs" color="content.secondary">
                      External IP
                    </Text>
                    <Code fontSize="sm">{service.externalIP}</Code>
                  </Box>
                )}
                <Box>
                  <Text fontSize="xs" color="content.secondary">
                    Age
                  </Text>
                  <Text>{service.age}</Text>
                </Box>
              </HStack>
            </Box>

            {service.ports && service.ports.length > 0 && (
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Ports
                </Text>
                <Table.Root size="sm">
                  <Table.Header>
                    <Table.Row>
                      <Table.ColumnHeader>Name</Table.ColumnHeader>
                      <Table.ColumnHeader>Protocol</Table.ColumnHeader>
                      <Table.ColumnHeader>Port</Table.ColumnHeader>
                      <Table.ColumnHeader>Target Port</Table.ColumnHeader>
                      <Table.ColumnHeader>Node Port</Table.ColumnHeader>
                    </Table.Row>
                  </Table.Header>
                  <Table.Body>
                    {service.ports.map((port, idx) => (
                      <Table.Row key={idx}>
                        <Table.Cell>
                          <Text fontFamily="mono" fontSize="sm">
                            {port.name || '-'}
                          </Text>
                        </Table.Cell>
                        <Table.Cell>
                          <Badge variant="subtle">{port.protocol}</Badge>
                        </Table.Cell>
                        <Table.Cell>
                          <Code fontSize="sm">{port.port}</Code>
                        </Table.Cell>
                        <Table.Cell>
                          <Code fontSize="sm">{port.targetPort || '-'}</Code>
                        </Table.Cell>
                        <Table.Cell>
                          <Code fontSize="sm">{port.nodePort || '-'}</Code>
                        </Table.Cell>
                      </Table.Row>
                    ))}
                  </Table.Body>
                </Table.Root>
              </Box>
            )}

            {service.selector && Object.keys(service.selector).length > 0 && (
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Selector
                </Text>
                <HStack gap={2} flexWrap="wrap">
                  {Object.entries(service.selector).map(([k, v]) => (
                    <Badge key={k} variant="subtle" colorPalette="cyan" fontFamily="mono">
                      {k}={v}
                    </Badge>
                  ))}
                </HStack>
              </Box>
            )}

            {service.labels && Object.keys(service.labels).length > 0 && (
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Labels
                </Text>
                <HStack gap={2} flexWrap="wrap">
                  {Object.entries(service.labels).map(([k, v]) => (
                    <Badge key={k} variant="outline" fontFamily="mono">
                      {k}={v}
                    </Badge>
                  ))}
                </HStack>
              </Box>
            )}

            {service.annotations && Object.keys(service.annotations).length > 0 && (
              <Box>
                <Text fontWeight="semibold" mb={2}>
                  Annotations
                </Text>
                <VStack align="stretch" gap={1}>
                  {Object.entries(service.annotations).map(([k, v]) => (
                    <Box key={k} p={2} bg="bg.subtle" borderRadius="md">
                      <Text fontSize="xs" color="content.secondary" fontFamily="mono">
                        {k}
                      </Text>
                      <Text fontSize="sm" fontFamily="mono" wordBreak="break-all">
                        {v}
                      </Text>
                    </Box>
                  ))}
                </VStack>
              </Box>
            )}
          </VStack>
        </Box>
      </Box>
    </Box>
  )
}

// Get color palette for service type
function getServiceTypeColor(type: string): string {
  switch (type) {
    case 'ClusterIP':
      return 'blue'
    case 'NodePort':
      return 'purple'
    case 'LoadBalancer':
      return 'green'
    case 'ExternalName':
      return 'orange'
    default:
      return 'gray'
  }
}

export function ServicesTab({ namespace, isAdmin }: ServicesTabProps) {
  const queryClient = useQueryClient()
  const [currentPage, setCurrentPage] = useState(1)
  const [searchQuery, setSearchQuery] = useState('')
  const [viewService, setViewService] = useState<K8sService | null>(null)

  // Fetch services
  const {
    data: servicesData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['services', namespace],
    queryFn: () => k8sApi.services(namespace || undefined),
    retry: false,
  })

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: ({ namespace, name }: { namespace: string; name: string }) =>
      k8sApi.deleteK8sService(namespace, name),
    onSuccess: (_, variables) => {
      toaster.create({
        title: 'Service deleted',
        description: `${variables.name} deleted successfully`,
        type: 'success',
      })
      queryClient.invalidateQueries({ queryKey: ['services'] })
    },
    onError: (error: Error) => {
      toaster.create({
        title: 'Failed to delete service',
        description: error.message,
        type: 'error',
      })
    },
  })

  const handleDelete = (namespace: string, name: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete service "${name}"?\n\nThis may break connectivity to pods using this service.`,
      )
    ) {
      deleteMutation.mutate({ namespace, name })
    }
  }

  const allServices = servicesData?.services || []

  // Filter based on search query
  const filteredServices = allServices.filter((s) => {
    if (!searchQuery.trim()) return true
    const query = searchQuery.toLowerCase()
    return (
      s.name.toLowerCase().includes(query) ||
      s.namespace.toLowerCase().includes(query) ||
      s.type.toLowerCase().includes(query) ||
      (s.clusterIP && s.clusterIP.toLowerCase().includes(query))
    )
  })

  // Pagination
  const totalPages = Math.ceil(filteredServices.length / PAGE_SIZE)
  const startIndex = (currentPage - 1) * PAGE_SIZE
  const endIndex = startIndex + PAGE_SIZE
  const services = filteredServices.slice(startIndex, endIndex)

  const handleSearchChange = (query: string) => {
    setSearchQuery(query)
    setCurrentPage(1)
  }

  const clearSearch = () => {
    setSearchQuery('')
    setCurrentPage(1)
  }

  return (
    <VStack align="stretch" gap={4}>
      <Flex justify="space-between" align="center">
        <Box position="relative" w="300px">
          <HStack
            position="absolute"
            left={3}
            top="50%"
            transform="translateY(-50%)"
            zIndex={1}
            pointerEvents="none"
          >
            <SearchIcon size={16} color="gray" />
          </HStack>
          <Input
            size="sm"
            pl={10}
            pr={searchQuery ? 10 : 3}
            placeholder="Search by name, namespace, type, IP..."
            value={searchQuery}
            onChange={(e) => handleSearchChange(e.target.value)}
          />
          {searchQuery && (
            <Button
              size="xs"
              variant="ghost"
              position="absolute"
              right={1}
              top="50%"
              transform="translateY(-50%)"
              onClick={clearSearch}
              aria-label="Clear search"
              minW="auto"
              h="auto"
              p={1}
            >
              <XIcon size={14} />
            </Button>
          )}
        </Box>
        <Button size="sm" onClick={() => refetch()}>
          <RefreshCwIcon size={16} />
          Refresh
        </Button>
      </Flex>

      <Card.Root>
        <Table.Root>
          <Table.Header>
            <Table.Row bg="bg.subtle">
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader>Namespace</Table.ColumnHeader>
              <Table.ColumnHeader>Type</Table.ColumnHeader>
              <Table.ColumnHeader>Cluster IP</Table.ColumnHeader>
              <Table.ColumnHeader>Ports</Table.ColumnHeader>
              <Table.ColumnHeader>Age</Table.ColumnHeader>
              <Table.ColumnHeader>Actions</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {isLoading ? (
              <Table.Row>
                <Table.Cell colSpan={7}>
                  <Flex justify="center" py={8}>
                    <Spinner />
                  </Flex>
                </Table.Cell>
              </Table.Row>
            ) : services.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={7}>
                  <VStack py={8} gap={2}>
                    <NetworkIcon size={32} color="gray" />
                    <Text textAlign="center" color="content.secondary">
                      {searchQuery
                        ? `No Services matching "${searchQuery}"`
                        : namespace
                          ? 'No Services in this namespace'
                          : 'No Services found'}
                    </Text>
                    {searchQuery && (
                      <Button size="sm" variant="ghost" onClick={clearSearch}>
                        Clear search
                      </Button>
                    )}
                  </VStack>
                </Table.Cell>
              </Table.Row>
            ) : (
              services.map((s) => (
                <Table.Row key={`${s.namespace}/${s.name}`}>
                  <Table.Cell>
                    <Text fontWeight="medium">{s.name}</Text>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge variant="outline">{s.namespace}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Badge colorPalette={getServiceTypeColor(s.type)}>{s.type}</Badge>
                  </Table.Cell>
                  <Table.Cell>
                    <Code fontSize="sm">{s.clusterIP || 'None'}</Code>
                  </Table.Cell>
                  <Table.Cell>
                    <Text fontSize="sm" fontFamily="mono" maxW="200px" truncate>
                      {formatPorts(s.ports)}
                    </Text>
                  </Table.Cell>
                  <Table.Cell>{s.age}</Table.Cell>
                  <Table.Cell>
                    <HStack gap={2}>
                      <Button size="xs" variant="ghost" onClick={() => setViewService(s)}>
                        <EyeIcon size={14} />
                        View
                      </Button>
                      {isAdmin && (
                        <Button
                          size="xs"
                          variant="ghost"
                          colorPalette="red"
                          loading={deleteMutation.isPending}
                          onClick={() => handleDelete(s.namespace, s.name)}
                        >
                          <Trash2Icon size={14} />
                          Delete
                        </Button>
                      )}
                    </HStack>
                  </Table.Cell>
                </Table.Row>
              ))
            )}
          </Table.Body>
        </Table.Root>
      </Card.Root>

      {/* Pagination */}
      {totalPages > 1 && (
        <Flex justify="space-between" align="center" pt={2}>
          <Text fontSize="sm" color="content.secondary">
            Showing {startIndex + 1}-{Math.min(endIndex, filteredServices.length)} of{' '}
            {filteredServices.length} Services
            {searchQuery && ` (filtered from ${allServices.length})`}
          </Text>
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeftIcon size={16} />
              Previous
            </Button>
            <HStack gap={1}>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                let page: number
                if (totalPages <= 5) {
                  page = i + 1
                } else if (currentPage <= 3) {
                  page = i + 1
                } else if (currentPage >= totalPages - 2) {
                  page = totalPages - 4 + i
                } else {
                  page = currentPage - 2 + i
                }
                return (
                  <Button
                    key={page}
                    size="sm"
                    variant={page === currentPage ? 'solid' : 'outline'}
                    colorPalette={page === currentPage ? 'blue' : undefined}
                    onClick={() => setCurrentPage(page)}
                    minW="40px"
                  >
                    {page}
                  </Button>
                )
              })}
            </HStack>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
            >
              Next
              <ChevronRightIcon size={16} />
            </Button>
          </HStack>
        </Flex>
      )}

      {/* View Modal */}
      {viewService && (
        <ViewServiceModal service={viewService} onClose={() => setViewService(null)} />
      )}
    </VStack>
  )
}
